import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SubscriptionPlansData } from '@/features/subscription/types';
import { SubscriptionPlanPeriodicity } from '@/shared/api/types/generated';
import { useCreateStripeCheckoutSessionMutation } from '@/shared/hooks/queries/generated';

interface UseSubscriptionSubscribeRedirectOptions {
  subscriptionPlans: SubscriptionPlansData | null;
  subscriptionPlansLoading: boolean;
}

export const useSubscriptionSubscribeRedirect = ({
  subscriptionPlans,
  subscriptionPlansLoading,
}: UseSubscriptionSubscribeRedirectOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const createCheckoutSession = useCreateStripeCheckoutSessionMutation();

  const action = searchParams.get('action');
  const slug = searchParams.get('slug');
  const billingPeriodParam = searchParams.get('billing_period');

  const cleanSearchParams = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('action');
      prev.delete('slug');
      return prev;
    });
  }, [setSearchParams]);

  useEffect(() => {
    // Wait for data to be loaded
    if (isLoading || subscriptionPlansLoading || !subscriptionPlans) {
      return;
    }

    if (!action || !billingPeriodParam || !slug) {
      // No relevant URL params, do nothing
      return;
    }

    // Check for action parameter
    if (action !== 'subscribe') {
      // Unknown action, do nothing
      return;
    }

    // Get billing_period from URL
    let billing_period: SubscriptionPlanPeriodicity;
    switch (billingPeriodParam) {
      case 'monthly':
        billing_period = 'monthly';
        break;
      case 'yearly':
        billing_period = 'yearly';
        break;
      default:
        // Invalid billing_period value
        return;
    }

    // Find the plan and execute subscription
    let planToSubscribeId: string | undefined;

    // Check classic and pro plans
    if (slug === 'recop-beginner' && subscriptionPlans.beginner) {
      planToSubscribeId = subscriptionPlans.beginner.id;
    } else if (slug === 'recop-classic' && subscriptionPlans.classic) {
      planToSubscribeId = subscriptionPlans.classic.id;
    } else if (slug === 'recop-pro' && subscriptionPlans.pro) {
      planToSubscribeId = subscriptionPlans.pro.id;
    } else {
      // Check custom plans
      const customPlan = subscriptionPlans.customPlans.find((plan) => plan.id === slug);
      if (customPlan) {
        planToSubscribeId = customPlan.id;
      }
    }

    // If no plan found, return early
    if (!planToSubscribeId) {
      return;
    }

    // Indicate loading state
    setIsLoading(true);

    // Trigger subscription
    createCheckoutSession
      .mutateAsync({
        input: {
          planId: planToSubscribeId,
          periodicity: billing_period === 'monthly' ? 'monthly' : 'yearly',
          successUrl: `${window.location.origin}/subscription?success=true&billing_period=${billing_period}`,
          cancelUrl: `${window.location.origin}/subscription?billing_period=${billing_period}`,
        },
      })
      .then((res) => {
        const redirectUrl = res.createStripeCheckoutSession.url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      })
      .catch((err) => {
        console.error('Error creating checkout session:', err);
      })
      .finally(() => {
        setIsLoading(false);
        cleanSearchParams();
      });
  }, [
    isLoading,
    action,
    slug,
    billingPeriodParam,
    subscriptionPlans,
    subscriptionPlansLoading,
    createCheckoutSession,
    setIsLoading,
    cleanSearchParams,
  ]);

  return { isLoading };
};
